import { useCallback, useEffect, useMemo, useState } from 'react';

// Web Admin → Results documents: add a document to the Results Hub, fix how
// archived results PDFs are filed there (hill, dates, label, class, type), or
// hide one. The PDF itself never changes. Every save is kept with who made it
// (backend/site_admin_routes.py, results_archive_edits). Added documents'
// text is read for the hub's search by backend/archive_upload.py.

const FLAG_TEXT = {
  'no-date': 'No date',
  'hill-looks-wrong': 'Hill name looks wrong',
  'end-before-start': 'End date before start',
};

const TYPE_TEXT = { event: 'Event results', 'season-standings': 'Season standings' };

const SOURCE_TEXT = {
  'wix-legacy': 'old website',
  'drive-embedded': 'old website (Drive)',
  'drive-legacy': 'Drive archive',
  jasper: 'Jasper report',
  'season-close': 'season close',
  'user-upload': 'uploaded',
  'jamie-archive': "Jamie's archive",
  'web-admin': 'added in Web Admin',
};

const SEARCH_TEXT = {
  waiting: 'Not searchable yet',
  'no-text': 'No text found',
};

const MAX_BYTES = 20 * 1024 * 1024;

function fmtDate(iso) {
  return iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

function dates(doc) {
  if (!doc.event_date) return doc.doc_type === 'event' ? `${doc.year}, no date` : `${doc.year}${doc.year_end ? `–${doc.year_end}` : ''}`;
  return doc.event_date_end ? `${fmtDate(doc.event_date)} – ${fmtDate(doc.event_date_end)}` : fmtDate(doc.event_date);
}

function changeText(field, [from, to]) {
  const show = v => (v === null || v === '' ? '(empty)' : v === true ? 'yes' : v === false ? 'no' : String(v));
  const names = { hill: 'Hill', event_date: 'Date', event_date_end: 'End date', label: 'Label', category: 'Class', doc_type: 'Type', year: 'Year', hidden: 'Hidden' };
  return `${names[field] || field}: ${show(from)} → ${show(to)}`;
}

function History({ doc, session, onExpired }) {
  const [edits, setEdits] = useState(null);
  useEffect(() => {
    fetch(`/api/site-admin/results-docs/${doc.id}/history`, { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        setEdits(res.ok ? data.edits : []);
      })
      .catch(() => setEdits([]));
  }, [doc, session, onExpired]);
  if (!edits) return <p className="site-admin-meta">Loading changes…</p>;
  if (!edits.length) return <p className="site-admin-meta">Not changed in Web Admin yet.</p>;
  return (
    <ul className="site-admin-doc-history">
      {edits.map(e => (
        <li key={e.edited_at}>
          <span>{new Date(e.edited_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}, {e.edited_by}</span>
          <span>{Object.entries(e.changes).map(([f, v]) => changeText(f, v)).join('; ')}</span>
        </li>
      ))}
    </ul>
  );
}

function DocEditor({ doc, categories, session, onExpired, onSaved, onClose }) {
  const [draft, setDraft] = useState({
    hill: doc.hill || '', event_date: doc.event_date || '', event_date_end: doc.event_date_end || '',
    label: doc.label, category: doc.category || '', doc_type: doc.doc_type, year: String(doc.year), hidden: doc.hidden,
  });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [newHill, setNewHill] = useState(null);   // {name, suggestions} after a 409

  const set = key => e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft(d => ({ ...d, [key]: value }));
    setError('');
    setNewHill(null);
    setState('idle');
  };

  const save = async (override = {}, newHillOk = false) => {
    const body = { ...draft, ...override, new_hill_ok: newHillOk };
    if (body.event_date) delete body.year; else body.year = Number(body.year);
    setState('saving');
    setError('');
    const res = await fetch(`/api/site-admin/results-docs/${doc.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.session_token}` },
      body: JSON.stringify(body),
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) {
      setNewHill(null);
      setState('saved');
      onSaved(data);
    } else if (res && res.status === 409 && data.new_hill) {
      setNewHill({ name: body.hill, suggestions: data.suggestions || [] });
      setState('idle');
    } else {
      setError(data.error || 'Could not save. Please try again.');
      setState('idle');
    }
  };

  const pickHill = name => { setDraft(d => ({ ...d, hill: name })); save({ hill: name }); };

  return (
    <div className="site-admin-doc-editor">
      <div className="site-admin-pair">
        <label className="site-field">
          <span>Hill</span>
          <input type="text" value={draft.hill} onChange={set('hill')} list="site-admin-hill-names" maxLength={80} />
        </label>
        <label className="site-field">
          <span>Label in the Results Hub</span>
          <input type="text" value={draft.label} onChange={set('label')} maxLength={200} />
        </label>
      </div>
      <div className="site-admin-pair">
        <label className="site-field">
          <span>Date</span>
          <input type="date" value={draft.event_date} onChange={set('event_date')} />
        </label>
        <label className="site-field">
          <span>End date <span className="site-admin-help">(two-day meetings only)</span></span>
          <input type="date" value={draft.event_date_end} onChange={set('event_date_end')} disabled={!draft.event_date} />
        </label>
      </div>
      <div className="site-admin-pair">
        <label className="site-field">
          <span>Class</span>
          <select value={draft.category} onChange={set('category')}>
            <option value="">(none)</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="site-field">
          <span>Type</span>
          <select value={draft.doc_type} onChange={set('doc_type')}>
            {Object.entries(TYPE_TEXT).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>
      {!draft.event_date && (
        <label className="site-field site-admin-doc-year">
          <span>Season <span className="site-admin-help">(set automatically from the date when there is one)</span></span>
          <input type="number" value={draft.year} onChange={set('year')} min={1900} max={2100} />
        </label>
      )}
      <label className="site-admin-check">
        <input type="checkbox" checked={draft.hidden} onChange={set('hidden')} />
        Hide from the Results Hub (for duplicates or wrong documents; untick to bring it back)
      </label>

      {newHill && (
        <div className="site-admin-notice" role="alert">
          <p>No other document uses the hill name <strong>&ldquo;{newHill.name}&rdquo;</strong>, so the Results Hub would show it as a new venue.</p>
          <div className="site-admin-doc-actions">
            {newHill.suggestions.map(s => (
              <button key={s} type="button" className="site-btn site-btn--ink" onClick={() => pickHill(s)}>Use &ldquo;{s}&rdquo;</button>
            ))}
            <button type="button" className="site-link-button" onClick={() => save({}, true)}>Keep &ldquo;{newHill.name}&rdquo; as a new hill name</button>
          </div>
        </div>
      )}

      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">From the {SOURCE_TEXT[doc.source] || doc.source}, document {doc.id}</span>
        {error && <span className="site-field-error" role="alert">{error}</span>}
        {state === 'saved' && <span className="site-admin-ok" role="status">Saved</span>}
        <button type="button" className="site-link-button" onClick={onClose}>Close</button>
        <button type="button" className="site-btn site-btn--ink" onClick={() => save()} disabled={state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save'}
        </button>
      </div>
      <details className="site-admin-revisions">
        <summary>Changes made in Web Admin</summary>
        <History key={doc.id + (doc.label || '') + doc.hidden + doc.hill + doc.event_date} doc={doc} session={session} onExpired={onExpired} />
      </details>
    </div>
  );
}

function DocRow({ doc, editing, onEdit, ...editorProps }) {
  return (
    <li className={`site-admin-doc${doc.hidden ? ' site-admin-doc--hidden' : ''}`}>
      <div className="site-admin-doc-head">
        <div className="site-admin-doc-main">
          <strong>{doc.label}</strong>
          <span className="site-admin-meta">
            {doc.hill || 'No hill'} · {dates(doc)} · {TYPE_TEXT[doc.doc_type] || doc.doc_type}{doc.category ? ` · ${doc.category}` : ''}
          </span>
          {(doc.flags.length > 0 || doc.hidden || SEARCH_TEXT[doc.search]) && (
            <span className="site-admin-doc-flags">
              {doc.hidden && <span className="site-admin-flag site-admin-flag--muted">Hidden</span>}
              {SEARCH_TEXT[doc.search] && <span className="site-admin-flag site-admin-flag--muted">{SEARCH_TEXT[doc.search]}</span>}
              {doc.flags.map(f => <span key={f} className="site-admin-flag">{FLAG_TEXT[f] || f}</span>)}
            </span>
          )}
        </div>
        <div className="site-admin-doc-actions">
          <a href={`/api/results/archive/${doc.id}`} target="_blank" rel="noreferrer">Open PDF</a>
          <button type="button" className="site-btn site-btn--ink" onClick={onEdit} aria-expanded={editing}>
            {editing ? 'Editing' : 'Edit'}
          </button>
        </div>
      </div>
      {editing && <DocEditor doc={doc} {...editorProps} />}
    </li>
  );
}

function AddDocument({ categories, session, onExpired, onAdded, onCancel }) {
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState('event');     // 'event': one meeting; 'season': a whole season
  const [hill, setHill] = useState('');
  const [date, setDate] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [year, setYear] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newHill, setNewHill] = useState(null);   // {name, suggestions} after a 409

  const size = files.reduce((n, f) => n + f.size, 0);
  const isPdf = f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
  const filesError = files.length > 1 && files.some(isPdf) ? 'Choose one PDF on its own, or pictures only'
    : size > MAX_BYTES ? 'Those files come to over 20 MB' : '';
  // The hub shows the part after " — " on the meeting's button.
  const labelFor = hillName => (kind === 'event'
    ? `${hillName.trim() || 'Unknown hill'} ${date ? fmtDate(date) : year} — ${name.trim()}`
    : name.trim());
  const label = labelFor(hill);
  const ready = files.length && !filesError && name.trim() && (date || year) && !saving;

  const changed = setter => e => { setter(e.target.value); setError(''); setNewHill(null); };

  const submit = async (e, hillName = hill, newHillOk = false) => {
    e?.preventDefault();
    const form = new FormData();
    files.forEach(f => form.append('file', f));
    form.append('doc_type', kind === 'event' ? 'event' : 'season-standings');
    form.append('label', labelFor(hillName));
    form.append('category', category);
    if (kind === 'event') {
      form.append('hill', hillName.trim());
      if (date) {
        form.append('event_date', date);
        form.append('event_date_end', dateEnd);
      }
    }
    if (!(kind === 'event' && date)) form.append('year', year);
    if (newHillOk) form.append('new_hill_ok', 'true');
    setSaving(true);
    setError('');
    const res = await fetch('/api/site-admin/results-docs', {
      method: 'POST', headers: { Authorization: `Bearer ${session.session_token}` }, body: form,
    }).catch(() => null);
    setSaving(false);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) onAdded(data);
    else if (res && res.status === 409 && data.new_hill) setNewHill({ name: hillName, suggestions: data.suggestions || [] });
    else setError(data.error || 'Could not upload. Please try again.');
  };

  const pickHill = h => { setHill(h); submit(null, h); };

  return (
    <form className="site-admin-doc-upload site-admin-doc-add" onSubmit={submit}>
      <label className="site-field">
        <span>The document <span className="site-admin-help">(one PDF, or a picture of each page in order: JPG or PNG, up to 20 MB in all)</span></span>
        <input type="file" multiple accept="application/pdf,.pdf,image/jpeg,image/png"
          onChange={e => { setFiles([...e.target.files]); setError(''); }} />
        {files.length > 1 && !filesError && <span className="site-admin-help">{files.length} pictures, made into one PDF in this order: {files.map(f => f.name).join(', ')}</span>}
        {filesError && <span className="site-field-error">{filesError}</span>}
      </label>
      <fieldset className="site-admin-doc-kind">
        <legend>What is it?</legend>
        <label className="site-admin-check">
          <input type="radio" name="kind" checked={kind === 'event'} onChange={() => setKind('event')} />
          Papers from one meeting (results, programme, press report)
        </label>
        <label className="site-admin-check">
          <input type="radio" name="kind" checked={kind === 'season'} onChange={() => setKind('season')} />
          About a whole season (championship table, newsletter, notes)
        </label>
      </fieldset>
      {kind === 'event' && (
        <>
          <div className="site-admin-pair">
            <label className="site-field">
              <span>Hill</span>
              <input type="text" value={hill} onChange={changed(setHill)} list="site-admin-hill-names" maxLength={80} />
            </label>
            <label className="site-field">
              <span>Name on its button <span className="site-admin-help">(e.g. Results, Programme, Press report)</span></span>
              <input type="text" value={name} onChange={changed(setName)} maxLength={60} />
            </label>
          </div>
          <div className="site-admin-pair">
            <label className="site-field">
              <span>Date of the meeting</span>
              <input type="date" value={date} onChange={changed(setDate)} />
            </label>
            <label className="site-field">
              <span>End date <span className="site-admin-help">(two-day meetings only)</span></span>
              <input type="date" value={dateEnd} onChange={changed(setDateEnd)} disabled={!date} />
            </label>
          </div>
        </>
      )}
      {kind === 'season' && (
        <label className="site-field">
          <span>Name on its button <span className="site-admin-help">(e.g. &ldquo;Newsletter, spring 1985&rdquo;)</span></span>
          <input type="text" value={name} onChange={changed(setName)} maxLength={120} />
        </label>
      )}
      <div className="site-admin-pair">
        {!(kind === 'event' && date) && (
          <label className="site-field">
            <span>Season <span className="site-admin-help">{kind === 'event' ? '(when the date isn’t known)' : ''}</span></span>
            <input type="number" value={year} onChange={changed(setYear)} min={1900} max={2100} />
          </label>
        )}
        <label className="site-field">
          <span>Class <span className="site-admin-help">{kind === 'season' ? '(leave as none for newsletters and notes: they go under “From the archive”)' : '(optional)'}</span></span>
          <select value={category} onChange={changed(setCategory)}>
            <option value="">(none)</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      {name.trim() && <p className="site-admin-meta">It will be filed as &ldquo;{label}&rdquo;.</p>}

      {newHill && (
        <div className="site-admin-notice" role="alert">
          <p>No other document uses the hill name <strong>&ldquo;{newHill.name}&rdquo;</strong>, so the Results Hub would show it as a new venue.</p>
          <div className="site-admin-doc-actions">
            {newHill.suggestions.map(h => (
              <button key={h} type="button" className="site-btn site-btn--ink" onClick={() => pickHill(h)}>Use &ldquo;{h}&rdquo;</button>
            ))}
            <button type="button" className="site-link-button" onClick={() => submit(null, newHill.name, true)}>Keep &ldquo;{newHill.name}&rdquo; as a new hill name</button>
          </div>
        </div>
      )}

      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">It appears in the Results Hub at once. Its words become searchable a few minutes later.</span>
        {error && <span className="site-field-error" role="alert">{error}</span>}
        <button type="button" className="site-link-button" onClick={onCancel}>Cancel</button>
        <button type="submit" className="site-btn site-btn--ink" disabled={!ready}>
          {saving ? 'Uploading…' : 'Add document'}
        </button>
      </div>
    </form>
  );
}

export default function ResultsDocs({ session, onExpired, Section }) {
  const [view, setView] = useState('all');   // 'all', 'attention' or a year (number)
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null);

  const load = useCallback(() => {
    const query = view === 'all' ? 'all=1' : view === 'attention' ? 'attention=1' : `year=${view}`;
    fetch(`/api/site-admin/results-docs?${query}`, { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Could not load');
        setData(body);
        setError('');
      })
      .catch(e => setError(e.message));
  }, [view, session, onExpired]);

  useEffect(() => { load(); }, [load]);

  const onSaved = updated => {
    setData(d => ({
      ...d,
      // A fixed document stays in the "needs attention" list until the list
      // is reloaded, so it doesn't jump away while you're looking at it.
      docs: d.docs.map(x => (x.id === updated.id ? { ...x, ...updated } : x)),
    }));
  };

  const docs = useMemo(() => {
    if (!data) return [];
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return data.docs.filter(d => {
      if (flagFilter && !d.flags.includes(flagFilter)) return false;
      const text = `${d.label} ${d.hill || ''} ${d.category || ''} ${d.year} ${d.event_date || ''}`.toLowerCase();
      return words.every(w => text.includes(w));
    });
  }, [data, filter, flagFilter]);

  const flagCounts = useMemo(() => {
    const counts = {};
    (data?.docs || []).forEach(d => d.flags.forEach(f => { counts[f] = (counts[f] || 0) + 1; }));
    return counts;
  }, [data]);

  const summary = data ? `${data.attention_count} need attention` : '';

  const onAdded = doc => {
    setAdding(false);
    setAdded(doc);
    setFilter('');
    setFlagFilter('');
    setEditing(null);
    if (view === doc.year) load(); else setView(doc.year);
  };

  return (
    <Section id="results-docs" title="Results documents" summary={summary}>
      <p>How each archived results document is filed in the <a href="/results-hub" target="_blank" rel="noreferrer">Results Hub</a>: its hill, date, label and class. Fix a wrong hill name or a missing date here, or hide a duplicate. The PDF itself never changes, and every change is kept with who made it.</p>
      {!adding && (
        <p><button type="button" className="site-btn site-btn--ink" onClick={() => { setAdding(true); setAdded(null); }}>Add a document</button></p>
      )}
      {added && !adding && (
        <p className="site-admin-ok" role="status">
          Added &ldquo;{added.label}&rdquo;. <a href={`/api/results/archive/${added.id}`} target="_blank" rel="noreferrer">Open the PDF</a> to check it.
        </p>
      )}
      {adding && data && (
        <AddDocument categories={data.categories} session={session} onExpired={onExpired}
          onAdded={onAdded} onCancel={() => setAdding(false)} />
      )}
      {data?.ocr && (
        <p className="site-admin-help">
          {data.ocr.enabled
            ? `Scans are read by Google Cloud Vision: ${data.ocr.pages_used} of ${data.ocr.page_limit} pages used this month. Scans over that wait for next month.`
            : 'Scans are not read on this server (no Cloud Vision key), so only PDFs with their own text become searchable.'}
        </p>
      )}
      <datalist id="site-admin-hill-names">
        {(data?.hills || []).map(h => <option key={h.name} value={h.name}>{h.count ? `${h.count} documents` : 'calendar'}</option>)}
      </datalist>
      <div className="site-admin-doc-bar">
        <label className="site-field">
          <span>Show</span>
          <select value={view} onChange={e => { const v = e.target.value; setView(v === 'all' || v === 'attention' ? v : Number(v)); setEditing(null); setFlagFilter(''); }}>
            <option value="all">Everything{data ? ` (${data.seasons.reduce((n, s) => n + s.count, 0).toLocaleString()})` : ''}</option>
            <option value="attention">Needs attention{data ? ` (${data.attention_count})` : ''}</option>
            {(data?.seasons || []).map(s => (
              <option key={s.year} value={s.year}>{s.year} ({s.count}{s.attention ? `, ${s.attention} to check` : ''})</option>
            ))}
          </select>
        </label>
        <label className="site-field">
          <span>Find</span>
          <input type="search" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Hill, label or year" />
        </label>
        {view === 'attention' && Object.keys(flagCounts).length > 1 && (
          <label className="site-field">
            <span>Problem</span>
            <select value={flagFilter} onChange={e => setFlagFilter(e.target.value)}>
              <option value="">All</option>
              {Object.entries(flagCounts).map(([f, n]) => <option key={f} value={f}>{FLAG_TEXT[f] || f} ({n})</option>)}
            </select>
          </label>
        )}
      </div>
      {view === 'attention' && (
        <p className="site-admin-help">Most of the undated ones are scans covering a whole season or an unknown meeting. Only add a date when the document shows it.</p>
      )}
      {error && <p className="site-field-error">{error}</p>}
      {!data && !error && <p>Loading…</p>}
      {data && (
        <>
          <p className="site-admin-meta">{docs.length === data.docs.length ? `${docs.length.toLocaleString()} documents` : `${docs.length.toLocaleString()} of ${data.docs.length.toLocaleString()} documents`}</p>
          <ul className="site-admin-docs">
            {docs.slice(0, 300).map(d => (
              <DocRow key={d.id} doc={d} editing={editing === d.id} onEdit={() => setEditing(editing === d.id ? null : d.id)}
                categories={data.categories} session={session} onExpired={onExpired}
                onSaved={onSaved} onClose={() => setEditing(null)} />
            ))}
          </ul>
          {docs.length > 300 && <p className="site-admin-meta">Showing the first 300. Use Find to narrow the list.</p>}
        </>
      )}
    </Section>
  );
}
